import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/Header';
import { useCourses } from '../hooks/useCourses';
import { CourseCapsule, hasPracticeMaterials } from '../../../types/course';

function CapsuleRow({ capsule, courseSlug, index }: { capsule: CourseCapsule; courseSlug: string; index: number }) {
  const hasIntro = Boolean(capsule.layers.intro?.video || capsule.layers.intro?.description);
  const hasVideo = Boolean(capsule.layers.chords?.video);
  const hasPractice = hasPracticeMaterials(capsule);

  return (
    <Link
      to={`/courses/${courseSlug}/${capsule.slug}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-md)',
        padding: 'var(--space-sm) var(--space-md)',
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: 'var(--radius-sm)',
        transition: 'background var(--transition-fast)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{
        width: 28,
        height: 28,
        borderRadius: 'var(--radius-sm)',
        background: 'var(--color-accent-subtle)',
        color: 'var(--color-accent)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 13,
        flexShrink: 0,
      }}>
        {index + 1}
      </span>
      <span style={{ flex: 1, fontSize: 15, fontWeight: 500 }}>{capsule.title}</span>
      <span style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        {hasIntro && (
          <span title="הקדמה" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>🎬</span>
        )}
        {hasVideo && (
          <span title="ליווי" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>🎸</span>
        )}
        {hasPractice && (
          <span title="תרגול" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>📄</span>
        )}
      </span>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <polyline points="6 4 10 8 6 12" />
      </svg>
    </Link>
  );
}

export function CourseDetailPage() {
  const { courseSlug } = useParams<{ courseSlug: string }>();
  const { getCourseBySlug, loading, error } = useCourses();

  const course = courseSlug ? getCourseBySlug(courseSlug) : undefined;

  if (loading) {
    return (
      <>
        <Header />
        <div className="container" style={{ padding: 'var(--space-2xl)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
          Loading...
        </div>
      </>
    );
  }

  if (error || !course) {
    return (
      <>
        <Header />
        <div className="container" style={{ padding: 'var(--space-2xl)', textAlign: 'center' }}>
          <h2 style={{ marginBottom: 'var(--space-md)' }}>Course not found</h2>
          <Link to="/courses" style={{ color: 'var(--color-accent)' }}>Back to courses</Link>
        </div>
      </>
    );
  }

  const sortedCapsules = [...course.capsules].sort((a, b) => a.order - b.order);

  return (
    <>
      <Header />
      <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)', maxWidth: 700 }} dir="rtl">
        <div className="chip-breadcrumb" style={{ marginBottom: 'var(--space-md)' }}>
          <Link to="/courses">קורסים</Link> / {course.title}
        </div>

        <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 'var(--space-xs)' }}>{course.title}</h1>
        {course.artist && (
          <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-xs)' }}>
            {course.artist}
          </p>
        )}
        {course.description && (
          <p style={{ fontSize: 14, color: 'var(--color-text-muted)', marginBottom: 'var(--space-md)', lineHeight: 1.6 }}>
            {course.description}
          </p>
        )}

        <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 'var(--space-sm)' }}>
          {sortedCapsules.length} שיעורים
        </div>

        {/* Song list */}
        <div style={{
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-xs) 0',
        }}>
          {sortedCapsules.map((capsule, i) => (
            <div key={capsule.id}>
              {i > 0 && (
                <div style={{ height: 1, background: 'var(--color-border-subtle)', margin: '0 var(--space-md)' }} />
              )}
              <CapsuleRow capsule={capsule} courseSlug={course.slug} index={i} />
            </div>
          ))}
        </div>

        {sortedCapsules.length === 0 && (
          <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-2xl)' }}>
            No lessons in this course yet.
          </p>
        )}
      </div>
    </>
  );
}
